"use client";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Edit,
  Check,
  Trash,
  ArrowLeft,
  User,
  Store,
  Package,
  IndianRupee,
  Clock,
  Phone,
} from "lucide-react";
import { useOrder } from "../../../../hooks/useOrder";
import { useUser } from "../../../../hooks/useUser";
import { useFetchProductOwners } from "../../../../hooks/useFetchProduct";
import { Order } from "../../../../types/Order";
import Spinner from "../../../Spinner";
import LoadingComponent from "../../../form-components/LoadingComponent";
import CustomDropdown from "../../../CustomDropdown";

const OrderInfo: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { orders, loading, error, updateOrderStatus, fetchAllOrders } =
    useOrder();
  const { getUsers } = useUser();
  const { productOwners } = useFetchProductOwners();
  const [users, setUsers] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "completed", label: "Completed" },
    { value: "canceled", label: "Canceled" },
  ];

  useEffect(() => {
    fetchAllOrders();
    const fetchUsers = async () => {
      const fetchedUsers = await getUsers();
      setUsers(fetchedUsers);
    };
    fetchUsers();
  }, [fetchAllOrders, getUsers]);

  const order = orders.find((o) => o._id === orderId);
  const user = users.find((u) => u._id === order?.userId);
  const owner = productOwners.find((o) => o._id === order?.productOwnerId);

  useEffect(() => {
    if (order) {
      setSelectedStatus(order.status || "pending");
    }
  }, [order]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  if (loading) return <LoadingComponent />;
  if (error) return <div className="p-6 text-red-500">Error: {error}</div>;
  if (!order) return <div className="p-6">No order found</div>;

  const handleUpdate = async () => {
    if (!orderId) return;
    setUpdating(true);
    try {
      await updateOrderStatus(orderId, selectedStatus as Order["status"]);
      setIsEditing(false);
      fetchAllOrders();
    } catch (err) {
      console.error("Failed to update order status:", err);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = () => {
    console.log("Delete functionality not implemented yet.");
  };

  const statusColors: { [key: string]: string } = {
    pending: "bg-yellow-100 text-yellow-800",
    completed: "bg-green-100 text-green-800",
    canceled: "bg-red-100 text-red-800",
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Order #{order._id?.slice(-6)}
              </h1>
              <p className="text-sm text-gray-500">
                Details for order placed by{" "}
                {user
                  ? `${user.firstName} ${user.lastName || ""}`.trim()
                  : "Unknown User"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDelete}
              disabled
              className="p-2 rounded-full hover:bg-red-100 text-red-500 transition-colors disabled:text-gray-400 disabled:bg-transparent disabled:cursor-not-allowed"
            >
              <Trash size={20} />
            </button>
            {isEditing ? (
              <button
                onClick={handleUpdate}
                disabled={updating}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors disabled:bg-gray-400"
              >
                {updating ? <Spinner /> : <Check size={20} />}
                Save
              </button>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg flex items-center gap-2 hover:bg-gray-300 transition-colors"
              >
                <Edit size={20} />
                Edit
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <InfoCard title="Order Information" icon={<Package />}>
              <DetailItem label="Order ID" value={`#${order._id}`} />
              <DetailItem
                label="Product ID"
                value={`${order.productId} (${order.productSize})`}
              />
              <DetailItem label="Product Type" value={order.productType} />
              <DetailItem
                label="Quantity"
                value={order.productQuantity.toString()}
              />
            </InfoCard>
            <InfoCard title="Price Details" icon={<IndianRupee />}>
              <DetailItem
                label="Product Price"
                value={`₹${order.productPrice.toFixed(2)}`}
              />
              <DetailItem
                label="Loading Charge"
                value={`₹${order.loadingCharge.toFixed(2)}`}
              />
              <DetailItem
                label="Broker Charge"
                value={`₹${order.brokerCharge.toFixed(2)}`}
              />
              <DetailItem
                label="Platform Charge"
                value={`₹${order.platformCharge.toFixed(2)}`}
              />
              <hr className="my-2" />
              <DetailItem
                label="Total Amount"
                value={`₹${order.totalAmount.toFixed(2)}`}
                isTotal
              />
            </InfoCard>
          </div>
          <div className="lg:col-span-1 space-y-6">
            <InfoCard title="Order Status" icon={<Clock />}>
              {isEditing ? (
                <CustomDropdown
                  label="Status"
                  value={selectedStatus}
                  options={statusOptions}
                  isOpen={statusDropdownOpen}
                  setIsOpen={setStatusDropdownOpen}
                  onSelect={(value) => setSelectedStatus(value)}
                  isEditing={isEditing}
                  dropdownRef={statusDropdownRef}
                />
              ) : (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-semibold capitalize ${
                    statusColors[selectedStatus] || "bg-gray-100 text-gray-800"
                  }`}
                >
                  {statusOptions.find((opt) => opt.value === selectedStatus)
                    ?.label || selectedStatus}
                </span>
              )}
              <hr className="my-2" />
              <DetailItem
                label="Order Placed"
                value={formatDate(order.timestamp)}
              />
            </InfoCard>
            <InfoCard title="User Details" icon={<User />}>
              <DetailItem label="User ID" value={order.userId} />
              <DetailItem
                label="Name"
                value={
                  user
                    ? `${user.firstName} ${user.lastName || ""}`.trim()
                    : "Unknown User"
                }
              />
              <DetailItem
                label="Phone"
                value={user ? user.phone : "N/A"}
                icon={<Phone size={14} className="mr-2" />}
              />
            </InfoCard>
            <InfoCard title="Product Owner" icon={<Store />}>
              <DetailItem label="Owner ID" value={order.productOwnerId} />
              <DetailItem
                label="Name"
                value={owner ? owner.productOwnerName : "Unknown Owner"}
              />
              <DetailItem
                label="Phone"
                value={owner ? String(owner.phoneNumber) : "N/A"}
                icon={<Phone size={14} className="mr-2" />}
              />
            </InfoCard>
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, children }) => (
  <div className="bg-white p-6 rounded-lg shadow">
    <div className="flex items-center gap-3 mb-4">
      <div className="text-[#3c0b0b]">{icon}</div>
      <h3 className="text-lg font-semibold text-gray-700">{title}</h3>
    </div>
    <div className="space-y-3">{children}</div>
  </div>
);

const DetailItem: React.FC<{
  label: string;
  value: string;
  isTotal?: boolean;
  icon?: React.ReactNode;
}> = ({ label, value, isTotal, icon }) => (
  <div
    className={`flex justify-between items-center ${
      isTotal ? "text-lg font-bold" : "text-sm"
    }`}
  >
    <p className="text-gray-500 flex items-center">
      {icon}
      {label}
    </p>
    <p className="font-medium text-gray-800">{value}</p>
  </div>
);

export default OrderInfo;
